/**
 * Annotator — screenshot annotation modal.
 *
 * v0.6.0: rectangle, arrow, freehand, text. Custom canvas, no external lib.
 * v0.10.0: + highlight (semi-transparent fill), blur (pixelate sensitive
 * regions), full undo/redo stack, native color picker alongside swatches.
 *
 * Save rasterizes the original image + every shape onto a fresh canvas
 * and returns a PNG Blob. Blurs are always sampled from the source image
 * pixels (not the canvas), so strokes/text/highlights drawn near a blurred
 * region don't get re-blurred by accident.
 */

import { h } from 'preact'
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks'

import type { StringKey } from './i18n'

type Tool = 'rectangle' | 'arrow' | 'freehand' | 'text' | 'highlight' | 'blur'

interface BaseShape {
  color: string
  lineWidth: number
}

interface RectShape extends BaseShape {
  kind: 'rectangle'
  x: number
  y: number
  w: number
  h: number
}

interface ArrowShape extends BaseShape {
  kind: 'arrow'
  x1: number
  y1: number
  x2: number
  y2: number
}

interface FreehandShape extends BaseShape {
  kind: 'freehand'
  points: Array<{ x: number; y: number }>
}

interface TextShape extends BaseShape {
  kind: 'text'
  x: number
  y: number
  text: string
  fontSize: number
}

interface HighlightShape extends BaseShape {
  kind: 'highlight'
  x: number
  y: number
  w: number
  h: number
}

interface BlurShape extends BaseShape {
  kind: 'blur'
  x: number
  y: number
  w: number
  h: number
  /** Tile size in source-image pixels. Scales with canvas width. */
  tile: number
}

type Shape =
  | RectShape
  | ArrowShape
  | FreehandShape
  | TextShape
  | HighlightShape
  | BlurShape

const COLORS: string[] = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#ffffff']
const HIGHLIGHT_ALPHA = 0.35

// ---------------------------------------------------------------------------
// Drawing helpers
// ---------------------------------------------------------------------------

function drawShape(
  ctx: CanvasRenderingContext2D,
  shape: Shape,
  sourceImage: HTMLImageElement | null,
) {
  ctx.save()
  ctx.strokeStyle = shape.color
  ctx.fillStyle = shape.color
  ctx.lineWidth = shape.lineWidth
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  if (shape.kind === 'rectangle') {
    ctx.strokeRect(shape.x, shape.y, shape.w, shape.h)
  } else if (shape.kind === 'arrow') {
    drawArrow(ctx, shape.x1, shape.y1, shape.x2, shape.y2)
  } else if (shape.kind === 'freehand') {
    if (shape.points.length < 2) {
      ctx.restore()
      return
    }
    ctx.beginPath()
    ctx.moveTo(shape.points[0]!.x, shape.points[0]!.y)
    for (let i = 1; i < shape.points.length; i++) {
      ctx.lineTo(shape.points[i]!.x, shape.points[i]!.y)
    }
    ctx.stroke()
  } else if (shape.kind === 'text') {
    ctx.font = `bold ${shape.fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`
    ctx.textBaseline = 'top'
    const metrics = ctx.measureText(shape.text)
    const padding = 4
    const w = metrics.width + padding * 2
    const hh = shape.fontSize + padding * 2
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'
    ctx.fillRect(shape.x - padding, shape.y - padding, w, hh)
    ctx.fillStyle = shape.color
    ctx.fillText(shape.text, shape.x, shape.y)
  } else if (shape.kind === 'highlight') {
    // Semi-transparent filled rect. Multiply-ish blend so the underlying
    // pixels show through — typical marker-pen look.
    ctx.globalAlpha = HIGHLIGHT_ALPHA
    ctx.fillRect(
      normalizeStart(shape.x, shape.w),
      normalizeStart(shape.y, shape.h),
      Math.abs(shape.w),
      Math.abs(shape.h),
    )
  } else if (shape.kind === 'blur') {
    drawBlur(ctx, shape, sourceImage)
  }
  ctx.restore()
}

function normalizeStart(start: number, size: number): number {
  return size < 0 ? start + size : start
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
) {
  const headLen = 14
  const angle = Math.atan2(y2 - y1, x2 - x1)
  ctx.beginPath()
  ctx.moveTo(x1, y1)
  ctx.lineTo(x2, y2)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(x2, y2)
  ctx.lineTo(
    x2 - headLen * Math.cos(angle - Math.PI / 6),
    y2 - headLen * Math.sin(angle - Math.PI / 6),
  )
  ctx.lineTo(
    x2 - headLen * Math.cos(angle + Math.PI / 6),
    y2 - headLen * Math.sin(angle + Math.PI / 6),
  )
  ctx.closePath()
  ctx.fill()
}

/**
 * Pixelate a rectangular region by averaging tiles of the source image.
 * Always samples from the original `<img>` so blurs over text/strokes
 * still hide the underlying pixels, not the marks drawn on top.
 */
function drawBlur(
  ctx: CanvasRenderingContext2D,
  shape: BlurShape,
  sourceImage: HTMLImageElement | null,
) {
  if (!sourceImage) return
  const x = normalizeStart(shape.x, shape.w)
  const y = normalizeStart(shape.y, shape.h)
  const w = Math.abs(shape.w)
  const h = Math.abs(shape.h)
  if (w < 2 || h < 2) return
  const tile = Math.max(4, shape.tile)
  // Drop tiles aligned to the source image — pixelation is at native res
  // so the result is crisp regardless of CSS scale on the display canvas.
  for (let yy = y; yy < y + h; yy += tile) {
    for (let xx = x; xx < x + w; xx += tile) {
      const tw = Math.min(tile, x + w - xx)
      const th = Math.min(tile, y + h - yy)
      ctx.drawImage(
        sourceImage,
        xx,
        yy,
        tw,
        th, // source rect from the image
        xx,
        yy,
        tw,
        th, // dest rect on the canvas (same coords)
      )
    }
  }
  // Sampling a single tile-sized chunk and re-painting it at tile size
  // gives the mosaic. To get the visual look, downscale + upscale via
  // imageSmoothingDisabled.
  ctx.imageSmoothingEnabled = false
  for (let yy = y; yy < y + h; yy += tile) {
    for (let xx = x; xx < x + w; xx += tile) {
      const tw = Math.min(tile, x + w - xx)
      const th = Math.min(tile, y + h - yy)
      // Sample the top-left pixel of this tile from the source and stretch.
      ctx.drawImage(
        sourceImage,
        xx,
        yy,
        1,
        1, // single source pixel
        xx,
        yy,
        tw,
        th, // stretched dest tile
      )
    }
  }
  ctx.imageSmoothingEnabled = true
}

// ---------------------------------------------------------------------------
// Inline icons
// ---------------------------------------------------------------------------

const Icon = {
  rect: (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="2" y="3" width="12" height="10" fill="none" stroke="currentColor" stroke-width="1.5" />
    </svg>
  ),
  arrow: (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M2 8h11M9 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  ),
  pencil: (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M11.5 2.5l2 2L5 13H3v-2l8.5-8.5z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
    </svg>
  ),
  text: (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3 3h10M8 3v10M5 13h6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
    </svg>
  ),
  highlight: (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3 13l3-3L11 5l-2-2-5 5-3 3v2h2zM10 4l2 2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M2 14h12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
    </svg>
  ),
  blur: (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="2" y="2" width="4" height="4" fill="currentColor" opacity="0.85" />
      <rect x="7" y="2" width="3" height="3" fill="currentColor" opacity="0.55" />
      <rect x="11" y="2" width="3" height="4" fill="currentColor" opacity="0.4" />
      <rect x="2" y="7" width="3" height="3" fill="currentColor" opacity="0.6" />
      <rect x="6" y="7" width="3" height="3" fill="currentColor" opacity="0.85" />
      <rect x="10" y="7" width="4" height="3" fill="currentColor" opacity="0.5" />
      <rect x="2" y="11" width="4" height="3" fill="currentColor" opacity="0.4" />
      <rect x="7" y="11" width="3" height="3" fill="currentColor" opacity="0.65" />
      <rect x="11" y="11" width="3" height="3" fill="currentColor" opacity="0.85" />
    </svg>
  ),
  undo: (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4 7l3-3M4 7l3 3M4 7h6a3 3 0 0 1 0 6H7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  ),
  redo: (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M12 7l-3-3M12 7l-3 3M12 7H6a3 3 0 0 0 0 6h2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  ),
  trash: (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3 4h10M6 4V2.5h4V4M5 4l.5 9h5L11 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  ),
  close: (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
    </svg>
  ),
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface AnnotatorProps {
  imageBlob: Blob
  strings: Record<StringKey, string>
  onSave: (annotated: Blob) => void
  onCancel: () => void
}

export function Annotator({ imageBlob, strings, onSave, onCancel }: AnnotatorProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const [tool, setTool] = useState<Tool>('rectangle')
  const [color, setColor] = useState<string>(COLORS[0]!)
  const [shapes, setShapes] = useState<Shape[]>([])
  const [past, setPast] = useState<Shape[][]>([])
  const [future, setFuture] = useState<Shape[][]>([])
  const isDrawingRef = useRef(false)
  const [draftShape, setDraftShape] = useState<Shape | null>(null)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const scaleRef = useRef(1)

  function addShape(shape: Shape) {
    setShapes((s) => {
      // Use the functional setState so we read the most recent shape list
      // even when this fires from a stale-closure event handler. The past
      // stack captures `s` here (the truly current state).
      setPast((p) => [...p, s])
      return [...s, shape]
    })
    setFuture([])
  }

  function clearShapes() {
    setShapes((s) => {
      setPast((p) => [...p, s])
      return []
    })
    setFuture([])
  }

  function undo() {
    setPast((p) => {
      if (p.length === 0) return p
      const prev = p[p.length - 1]!
      setShapes((s) => {
        setFuture((f) => [s, ...f])
        return prev
      })
      return p.slice(0, -1)
    })
  }

  function redo() {
    setFuture((f) => {
      if (f.length === 0) return f
      const next = f[0]!
      setShapes((s) => {
        setPast((p) => [...p, s])
        return next
      })
      return f.slice(1)
    })
  }

  // Esc handler uses useLayoutEffect (not useEffect) so the listener is
  // attached SYNCHRONOUSLY after DOM commit, BEFORE paint. Tests (and
  // fast human users) that press Escape the moment the annotator becomes
  // visible would otherwise hit a brief window where the listener isn't
  // yet attached. The parent Modal's keydown handler bails when it sees
  // .annotator-backdrop in the shadow root, so we don't double-fire.
  useLayoutEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCancel()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  // Cmd/Ctrl-Z = undo, Cmd/Ctrl-Shift-Z (or Cmd/Ctrl-Y) = redo. Re-binds
  // on each state mutation to capture fresh shapes/past/future closures.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey
      if (!mod) return
      const k = e.key.toLowerCase()
      if (k === 'z') {
        e.preventDefault()
        e.stopPropagation()
        if (e.shiftKey) redo()
        else undo()
      } else if (k === 'y') {
        e.preventDefault()
        e.stopPropagation()
        redo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [past, future, shapes])

  useEffect(() => {
    const url = URL.createObjectURL(imageBlob)
    const img = new Image()
    img.onload = () => {
      imageRef.current = img
      setImageLoaded(true)
    }
    img.src = url
    return () => URL.revokeObjectURL(url)
  }, [imageBlob])

  // useLayoutEffect (not useEffect) so the canvas's width/height/style
  // are set SYNCHRONOUSLY after Preact's render commit and BEFORE the
  // browser's paint. Without this, the canvas first mounts at the HTML
  // default 300×150 and only resizes to the image's dimensions on the
  // next microtask — a window during which Playwright's
  // `canvas.boundingBox()` can read the stale layout. Tests then fire
  // mouse events at coordinates that no longer land inside the canvas,
  // mousedown is dispatched to the wrap/backdrop instead, no draftShape
  // is created, and `.annotator-count` stays at 0 → flake.
  useLayoutEffect(() => {
    if (
      !imageLoaded ||
      !canvasRef.current ||
      !imageRef.current ||
      !containerRef.current
    ) {
      return
    }
    const img = imageRef.current
    const container = containerRef.current
    const maxW = container.clientWidth - 16
    const maxH = window.innerHeight * 0.65
    const fitScale = Math.min(maxW / img.width, maxH / img.height, 1)
    scaleRef.current = fitScale

    const canvas = canvasRef.current
    canvas.width = img.width
    canvas.height = img.height
    canvas.style.width = `${img.width * fitScale}px`
    canvas.style.height = `${img.height * fitScale}px`
    redraw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageLoaded])

  useEffect(() => {
    redraw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapes, draftShape])

  function redraw() {
    const canvas = canvasRef.current
    const img = imageRef.current
    if (!canvas || !img) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0)
    for (const s of shapes) drawShape(ctx, s, img)
    if (draftShape) drawShape(ctx, draftShape, img)
  }

  function getCanvasCoords(e: MouseEvent) {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    return {
      x: (e.clientX - rect.left) / scaleRef.current,
      y: (e.clientY - rect.top) / scaleRef.current,
    }
  }

  const handleMouseDown = (e: MouseEvent) => {
    if (!imageLoaded) return
    const { x, y } = getCanvasCoords(e)
    const canvasW = canvasRef.current!.width
    const lineWidth = Math.max(3, Math.round(canvasW / 400))
    const blurTile = Math.max(8, Math.round(canvasW / 80))

    if (tool === 'text') {
      const text = window.prompt(strings['annotator.text_prompt'])
      if (text && text.trim()) {
        addShape({
          kind: 'text',
          x,
          y,
          text: text.trim(),
          color,
          fontSize: Math.max(16, Math.round(canvasW / 50)),
          lineWidth: 1,
        })
      }
      return
    }

    isDrawingRef.current = true
    if (tool === 'rectangle') {
      setDraftShape({ kind: 'rectangle', x, y, w: 0, h: 0, color, lineWidth })
    } else if (tool === 'arrow') {
      setDraftShape({ kind: 'arrow', x1: x, y1: y, x2: x, y2: y, color, lineWidth })
    } else if (tool === 'freehand') {
      setDraftShape({ kind: 'freehand', points: [{ x, y }], color, lineWidth })
    } else if (tool === 'highlight') {
      setDraftShape({
        kind: 'highlight',
        x,
        y,
        w: 0,
        h: 0,
        color: color === '#ffffff' ? '#fde047' : color,
        lineWidth: 1,
      })
    } else if (tool === 'blur') {
      setDraftShape({
        kind: 'blur',
        x,
        y,
        w: 0,
        h: 0,
        color: '#000000',
        lineWidth: 0,
        tile: blurTile,
      })
    }
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDrawingRef.current) return
    const { x, y } = getCanvasCoords(e)
    // Functional setState: read the most recent draftShape rather than the
    // closure-captured value. Playwright (and fast humans) can fire
    // mousedown→mousemove faster than Preact commits the re-render, leaving
    // the closure's `draftShape` at the previous frame (often null right
    // after mousedown). audit R6 e2e regression fix.
    setDraftShape((current) => {
      if (!current) return current
      if (
        current.kind === 'rectangle' ||
        current.kind === 'highlight' ||
        current.kind === 'blur'
      ) {
        return { ...current, w: x - current.x, h: y - current.y }
      }
      if (current.kind === 'arrow') {
        return { ...current, x2: x, y2: y }
      }
      if (current.kind === 'freehand') {
        return { ...current, points: [...current.points, { x, y }] }
      }
      return current
    })
  }

  const handleMouseUp = () => {
    // Same closure-staleness concern as handleMouseMove: read the
    // committed draft via the functional setter rather than rely on the
    // capture from this render.
    setDraftShape((current) => {
      if (isDrawingRef.current && current) {
        const isTiny =
          ((current.kind === 'rectangle' ||
            current.kind === 'highlight' ||
            current.kind === 'blur') &&
            Math.abs(current.w) < 4 &&
            Math.abs(current.h) < 4) ||
          (current.kind === 'arrow' &&
            Math.hypot(
              current.x2 - current.x1,
              current.y2 - current.y1,
            ) < 4) ||
          (current.kind === 'freehand' && current.points.length < 3)
        if (!isTiny) {
          addShape(current)
        }
      }
      return null
    })
    isDrawingRef.current = false
  }

  const handleSave = async () => {
    const canvas = canvasRef.current
    if (!canvas) return
    setSaving(true)
    try {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/png', 0.92),
      )
      if (blob) onSave(blob)
    } finally {
      setSaving(false)
    }
  }

  const tools: Array<{ id: Tool; icon: typeof Icon.rect; label: string }> = [
    { id: 'rectangle', icon: Icon.rect, label: strings['annotator.tool.rectangle'] },
    { id: 'arrow', icon: Icon.arrow, label: strings['annotator.tool.arrow'] },
    { id: 'freehand', icon: Icon.pencil, label: strings['annotator.tool.freehand'] },
    { id: 'text', icon: Icon.text, label: strings['annotator.tool.text'] },
    { id: 'highlight', icon: Icon.highlight, label: strings['annotator.tool.highlight'] },
    { id: 'blur', icon: Icon.blur, label: strings['annotator.tool.blur'] },
  ]

  return (
    <div
      class="annotator-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div class="annotator" role="dialog" aria-modal="true" aria-label={strings['annotator.title']}>
        <div class="annotator-header">
          <span>{strings['annotator.title']}</span>
          <button
            type="button"
            class="modal-close"
            aria-label={strings['form.close']}
            onClick={onCancel}
          >
            {Icon.close}
          </button>
        </div>

        <div class="annotator-toolbar">
          <div class="annotator-tools">
            {tools.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTool(t.id)}
                title={t.label}
                aria-label={t.label}
                aria-pressed={tool === t.id}
                class={`annotator-tool ${tool === t.id ? 'is-active' : ''}`}
              >
                {t.icon}
              </button>
            ))}
          </div>

          <span class="annotator-sep" />

          <div class="annotator-colors">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                aria-label={c}
                aria-pressed={color === c}
                class={`annotator-color ${color === c ? 'is-active' : ''}`}
                style={{ backgroundColor: c }}
              />
            ))}
            <label class="annotator-color annotator-color-picker" title={strings['annotator.color_picker']}>
              <input
                type="color"
                value={color}
                onInput={(e) => setColor((e.target as HTMLInputElement).value)}
                aria-label={strings['annotator.color_picker']}
              />
            </label>
          </div>

          <span class="annotator-sep" />

          <button
            type="button"
            class="annotator-btn"
            onClick={undo}
            disabled={past.length === 0}
            title={strings['annotator.undo']}
          >
            {Icon.undo}
            <span>{strings['annotator.undo']}</span>
          </button>
          <button
            type="button"
            class="annotator-btn"
            onClick={redo}
            disabled={future.length === 0}
            title={strings['annotator.redo']}
          >
            {Icon.redo}
            <span>{strings['annotator.redo']}</span>
          </button>
          <button
            type="button"
            class="annotator-btn"
            onClick={clearShapes}
            disabled={shapes.length === 0}
          >
            {Icon.trash}
            <span>{strings['annotator.clear']}</span>
          </button>

          <span class="annotator-spacer" />

          <span class="annotator-count">
            {shapes.length} {strings['annotator.count_suffix']}
          </span>
        </div>

        <div ref={containerRef} class="annotator-canvas-wrap">
          {!imageLoaded ? (
            <span class="annotator-loading">{strings['annotator.loading']}</span>
          ) : (
            <canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              class="annotator-canvas"
            />
          )}
        </div>

        <div class="annotator-footer">
          <button type="button" class="btn" onClick={onCancel}>
            {strings['form.cancel']}
          </button>
          <button
            type="button"
            class="btn btn--primary"
            onClick={handleSave}
            disabled={saving || !imageLoaded}
          >
            {saving ? strings['annotator.applying'] : strings['annotator.apply']}
          </button>
        </div>
      </div>
    </div>
  )
}
